import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { BREEDS, COLORS } from '../data/mock';
import { puppyService } from '../services/api';
import { X, Upload, Trash2, Loader2, AlertTriangle, Sparkles } from 'lucide-react';

export const PuppyForm = ({ isOpen, onClose, onSave, onDelete, editingPuppy, preSelectedBreed }) => {
  const [formData, setFormData] = useState({
    name: '',
    breed: '',
    color: '',
    sex: '',
    adultWeight: '',
    price: '',
    pricePedigree: '',
    priceType: 'fixed',
    status: 'disponible',
    images: [],
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    description: '',
    hasSpecialConditions: false,
    previousPrice: '',
    specialConditionsText: 'Hemos realizado un ajuste de valor para facilitar su incorporación a una familia adecuada, manteniendo intactos los estándares, cuidados y dedicación de nuestra línea.'
  });
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (editingPuppy) {
      const bd = editingPuppy.birthDate || '';
      const parts = bd.split('-');
      setFormData({
        ...editingPuppy,
        images: editingPuppy.images?.length ? editingPuppy.images : (editingPuppy.image ? [editingPuppy.image] : []),
        pricePedigree: editingPuppy.pricePedigree || '',
        hasPedigreeOption: !!editingPuppy.pricePedigree,
        priceType: editingPuppy.priceType || 'fixed',
        hasSpecialConditions: editingPuppy.hasSpecialConditions || false,
        hasDiscount: editingPuppy.hasDiscount || false,
        previousPrice: editingPuppy.previousPrice || '',
        birthYear: parts[0] || '',
        birthMonth: parts[1] || '',
        birthDay: parts[2] || '',
        specialConditionsText: editingPuppy.specialConditionsText || 'Hemos realizado un ajuste de valor para facilitar su incorporación a una familia adecuada, manteniendo intactos los estándares, cuidados y dedicación de nuestra línea.'
      });
    } else {
      setFormData({
        name: '',
        breed: preSelectedBreed || '',
        color: '',
        sex: '',
        adultWeight: '',
        price: '',
        pricePedigree: '',
        priceType: 'fixed',
        status: 'disponible',
        images: [],
        birthDay: '',
        birthMonth: '',
        birthYear: '',
        description: '',
        hasSpecialConditions: false,
        previousPrice: '',
        specialConditionsText: 'Hemos realizado un ajuste de valor para facilitar su incorporación a una familia adecuada, manteniendo intactos los estándares, cuidados y dedicación de nuestra línea.'
      });
    }
    setShowDeleteConfirm(false);
    setDeleting(false);
  }, [editingPuppy, isOpen]);

  // Compress image before upload - more aggressive compression
  const compressImage = (file, maxWidth = 800, quality = 0.5) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Scale down if too large
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (formData.images.length >= 4) {
      alert('Maximo 4 fotos');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      alert('La imagen es muy grande. Maximo 50MB.');
      return;
    }

    try {
      setUploading(true);
      const compressedFile = await compressImage(file, 800, 0.5);
      const imageUrl = await puppyService.uploadImage(compressedFile);
      setFormData({ ...formData, images: [...formData.images, imageUrl] });
    } catch (err) {
      void err;
      alert('Error al subir la imagen. Intenta con una imagen mas pequena.');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { hasPedigreeOption, birthDay, birthMonth, birthYear, ...submitData } = formData;
    const birthDate = (birthYear && birthMonth && birthDay) 
      ? `${birthYear}-${birthMonth}-${birthDay}` 
      : '';
    onSave({
      ...submitData,
      id: editingPuppy?.id || Date.now().toString(),
      price: parseFloat(formData.price) || 0,
      pricePedigree: formData.pricePedigree ? parseFloat(formData.pricePedigree) : null,
      previousPrice: formData.previousPrice ? parseFloat(formData.previousPrice) : null,
      birthDate,
      image: formData.images[0] || ''
    });
    onClose();
  };

  const handleDelete = async () => {
    if (!editingPuppy) return;
    
    setDeleting(true);
    try {
      await onDelete(editingPuppy.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      void err;
      alert('Error al eliminar el cachorro');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-amber-900">
            {editingPuppy ? 'Editar Cachorro' : 'Añadir Nuevo Cachorro'}
          </DialogTitle>
        </DialogHeader>
        
        {editingPuppy && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full flex items-center justify-center gap-2 bg-red-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-red-600 active:bg-red-700"
              >
                <Trash2 className="w-5 h-5" />
                Eliminar este cachorro
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">¿Eliminar "{editingPuppy.name}"?</span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                    disabled={deleting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Eliminando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Sí, eliminar
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Image Upload - up to 4 photos */}
          <div className="space-y-2">
            <Label className="text-amber-800">Fotos del Cachorro (max. 4)</Label>
            <div className="grid grid-cols-2 gap-3">
              {formData.images.map((img, index) => (
                <div key={`img-${img.slice(-20)}-${index}`} className="relative">
                  <img 
                    src={img} 
                    alt={`Foto ${index + 1}`} 
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {formData.images.length < 4 && (
                <label className="border-2 border-dashed border-amber-200 rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-amber-400 transition-colors">
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-amber-400" />
                      <span className="text-amber-600 text-sm mt-1">Anadir foto</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-amber-500">{formData.images.length}/4 fotos (se comprimen automaticamente)</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-amber-800">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ej: Luna"
                required
                className="border-amber-200 focus:border-amber-400"
              />
            </div>

            {/* Breed */}
            <div className="space-y-2">
              <Label className="text-amber-800">Raza</Label>
              <Select value={formData.breed} onValueChange={(value) => setFormData({...formData, breed: value})}>
                <SelectTrigger className="border-amber-200">
                  <SelectValue placeholder="Selecciona raza" />
                </SelectTrigger>
                <SelectContent>
                  {BREEDS.map((breed) => (
                    <SelectItem key={breed.id} value={breed.id}>
                      {breed.icon} {breed.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label className="text-amber-800">Color</Label>
              <Select value={formData.color} onValueChange={(value) => setFormData({...formData, color: value})}>
                <SelectTrigger className="border-amber-200">
                  <SelectValue placeholder="Selecciona color" />
                </SelectTrigger>
                <SelectContent>
                  {COLORS.map((color) => (
                    <SelectItem key={color} value={color}>{color}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sex */}
            <div className="space-y-2">
              <Label className="text-amber-800">Sexo</Label>
              <Select value={formData.sex} onValueChange={(value) => setFormData({...formData, sex: value})}>
                <SelectTrigger className="border-amber-200">
                  <SelectValue placeholder="Selecciona sexo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Macho">Macho</SelectItem>
                  <SelectItem value="Hembra">Hembra</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Adult Weight */}
            <div className="space-y-2">
              <Label htmlFor="weight" className="text-amber-800">Peso Adulto Aprox.</Label>
              <Input
                id="weight"
                value={formData.adultWeight}
                onChange={(e) => setFormData({...formData, adultWeight: e.target.value})}
                placeholder="Ej: 2-3 kg"
                required
                className="border-amber-200 focus:border-amber-400"
              />
            </div>

            {/* Price */}
            <div className="space-y-2 col-span-2">
              <Label className="text-amber-800">Precio</Label>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, priceType: 'fixed', price: formData.price || ''})}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    formData.priceType !== 'from' && formData.priceType !== 'consult'
                      ? 'bg-amber-600 text-white' 
                      : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  Precio fijo
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, priceType: 'from'})}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    formData.priceType === 'from' 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  Desde...
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, priceType: 'consult', price: 0, pricePedigree: ''})}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    formData.priceType === 'consult' 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  Consultar
                </button>
              </div>
              {formData.priceType !== 'consult' && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="price" className="text-amber-700 text-sm">Precio</Label>
                    <Input
                      id="price"
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      placeholder={formData.priceType === 'from' ? "Desde... (ej: 800)" : "Precio (ej: 1500)"}
                      required
                      className="border-amber-200 focus:border-amber-400"
                    />
                  </div>
                  
                  {/* Pedigree toggle */}
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="hasPedigreeOption"
                        checked={formData.hasPedigreeOption || false}
                        onChange={(e) => setFormData({...formData, hasPedigreeOption: e.target.checked, pricePedigree: e.target.checked ? formData.pricePedigree : ''})}
                        className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                      />
                      <Label htmlFor="hasPedigreeOption" className="text-amber-800 font-medium cursor-pointer text-sm">
                        Ofrecer opcion con pedigree
                      </Label>
                    </div>
                    {formData.hasPedigreeOption && (
                      <div className="mt-2">
                        <Label htmlFor="pricePedigree" className="text-amber-700 text-xs">Precio con pedigree</Label>
                        <Input
                          id="pricePedigree"
                          type="number"
                          value={formData.pricePedigree || ''}
                          onChange={(e) => setFormData({...formData, pricePedigree: e.target.value})}
                          placeholder="Precio con pedigree (ej: 1800)"
                          className="border-amber-200 focus:border-amber-400 mt-1"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
              {formData.priceType === 'consult' && (
                <p className="text-amber-600 text-sm italic">El precio se mostrara como "Consultar"</p>
              )}
            </div>

            {/* Birth Date - Day/Month/Year selectors for mobile compatibility */}
            <div className="space-y-2 col-span-2">
              <Label className="text-amber-800">Fecha de Nacimiento</Label>
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={formData.birthDay || ''}
                  onChange={(e) => setFormData({...formData, birthDay: e.target.value})}
                  className="h-12 px-3 rounded-md border border-amber-200 bg-white text-base focus:border-amber-400 focus:outline-none"
                >
                  <option value="">Dia</option>
                  {Array.from({length: 31}, (_, i) => i + 1).map(d => (
                    <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                  ))}
                </select>
                <select
                  value={formData.birthMonth || ''}
                  onChange={(e) => setFormData({...formData, birthMonth: e.target.value})}
                  className="h-12 px-3 rounded-md border border-amber-200 bg-white text-base focus:border-amber-400 focus:outline-none"
                >
                  <option value="">Mes</option>
                  {['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'].map((m, i) => (
                    <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
                  ))}
                </select>
                <select
                  value={formData.birthYear || ''}
                  onChange={(e) => setFormData({...formData, birthYear: e.target.value})}
                  className="h-12 px-3 rounded-md border border-amber-200 bg-white text-base focus:border-amber-400 focus:outline-none"
                >
                  <option value="">Ano</option>
                  {Array.from({length: 15}, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label className="text-amber-800">Estado</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                <SelectTrigger className="border-amber-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="disponible">Disponible</SelectItem>
                  <SelectItem value="reservado">Reservado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-amber-800">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Describe al cachorro..."
              rows={3}
              className="border-amber-200 focus:border-amber-400"
            />
          </div>

          {/* Special Conditions Section */}
          <div className="space-y-3 border-2 border-purple-200 rounded-xl p-4 bg-purple-50">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="hasSpecialConditions"
                checked={formData.hasSpecialConditions}
                onChange={(e) => setFormData({...formData, hasSpecialConditions: e.target.checked})}
                className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
              />
              <Label htmlFor="hasSpecialConditions" className="text-purple-800 font-semibold flex items-center gap-2 cursor-pointer">
                <Sparkles className="w-5 h-5 text-pink-500" />
                Selección con condiciones especiales
              </Label>
            </div>
            
            {formData.hasSpecialConditions && (
              <div className="space-y-3 pt-2">
                {/* Discount toggle */}
                <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-purple-100">
                  <input
                    type="checkbox"
                    id="hasDiscount"
                    checked={formData.hasDiscount || false}
                    onChange={(e) => setFormData({...formData, hasDiscount: e.target.checked})}
                    className="w-4 h-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                  <Label htmlFor="hasDiscount" className="text-purple-700 font-medium cursor-pointer text-sm">
                    Tiene descuento (mostrar valor anterior y actual)
                  </Label>
                </div>

                {formData.hasDiscount && (
                  <div className="space-y-2">
                    <Label htmlFor="previousPrice" className="text-purple-700">Valor anterior (€)</Label>
                    <Input
                      id="previousPrice"
                      type="number"
                      value={formData.previousPrice}
                      onChange={(e) => setFormData({...formData, previousPrice: e.target.value})}
                      placeholder="Ej: 2800"
                      className="border-purple-200 focus:border-purple-400"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="specialConditionsText" className="text-purple-700">Texto explicativo</Label>
                  <Textarea
                    id="specialConditionsText"
                    value={formData.specialConditionsText}
                    onChange={(e) => setFormData({...formData, specialConditionsText: e.target.value})}
                    rows={3}
                    className="border-purple-200 focus:border-purple-400 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white flex-1" disabled={uploading}>
              {editingPuppy ? 'Guardar Cambios' : 'Añadir Cachorro'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
